import React, { useEffect, useState } from 'react';
import { UserPreference, PreferenceItem } from '../../config';

interface UserPreferenceEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserPreferenceEditor: React.FC<UserPreferenceEditorProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState<PreferenceItem[]>([]);
  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPreferences();
    }
  }, [isOpen]);

  const loadPreferences = async () => {
    try {
      const userPreference = UserPreference.getInstance();
      await userPreference.load();
      const uiPreferences = userPreference.getUIPreferences();
      setPreferences(uiPreferences);
      setError('');
    } catch (err) {
      setError('Failed to load preferences');
      console.error('Error loading preferences:', err);
    }
  };

  const handlePreferenceChange = async (key: string, value: any) => {
    setIsSaving(true);
    try {
      const userPreference = UserPreference.getInstance();
      const success = userPreference.setValue(key, value);
      
      if (success) {
        const updatedPreferences = userPreference.getUIPreferences();
        setPreferences(updatedPreferences);
        setError('');
      } else {
        setError('Failed to save preference');
      }
    } catch (err) {
      setError('An error occurred while saving');
      console.error('Error saving preference:', err);
    }
    setIsSaving(false);
  };

  const handleReset = async () => {
    try {
      const userPreference = UserPreference.getInstance();
      userPreference.resetToDefaults();
      await loadPreferences();
      setError('');
    } catch (err) {
      setError('Failed to reset preferences');
      console.error('Error resetting preferences:', err);
    }
  };

  const renderPreferenceControl = (preference: PreferenceItem) => {
    switch (preference.type) {
      case 'boolean':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={preference.value}
              onChange={(e) => handlePreferenceChange(preference.key, e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        );
      
      case 'string':
        if (preference.params?.possibleValues) {
          return (
            <select
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-40 p-2.5"
              value={preference.value}
              onChange={(e) => handlePreferenceChange(preference.key, e.target.value)}
            >
              {preference.params.possibleValues.map((value: string) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          );
        }
        return null;
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Content */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-md" onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">User Preferences</h2>
              <p className="text-sm text-gray-600">Customize your application settings</p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="p-6">
            {error && (
              <div className="p-4 mb-4 text-sm text-red-800 bg-red-100 rounded-lg">
                {error}
              </div>
            )}
            
            <div className="space-y-6">
              {preferences.map((preference) => (
                <div key={preference.key} className="flex items-center justify-between">
                  <div>
                    <label className="text-base font-medium text-gray-900">
                      {preference.label}
                    </label>
                    <p className="text-sm text-gray-500">
                      Current value: {String(preference.value)}
                    </p>
                  </div>
                  {renderPreferenceControl(preference)}
                </div>
              ))}
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleReset}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserPreferenceEditor